import { useState, useEffect } from "react";
import {
  ChevronLeft,
  Phone,
  Mail,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCreateClient } from "../../../Hooks/useCreateClient";
import { clientService } from "../../../services/clientService";
import { toast } from "react-hot-toast";
import { ZipAutoFillInput } from "../components/ZipAutoFillInput";

export default function CreateClients() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const {
    createClient,
    isCreating,
  } = useCreateClient();

  const [title, setTitle] = useState("No title");
  const [showTitleDropdown, setShowTitleDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [street1, setStreet1] = useState("");
  const [street2, setStreet2] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("USA");
  const [password, setPassword] = useState("");

  const titles = ["Mr.", "Mrs.", "Ms.", "Miss", "Dr.", "Prof.", "No title"];

  useEffect(() => {
    if (isEditMode) {
      loadClientData();
    }
  }, [id]);

  const loadClientData = async () => {
    setLoading(true);
    try {
      const response = await clientService.getClientById(id);
      if (response.data.success && response.data.data) {
        const client = response.data.data;
        setTitle(client.title || "No title");
        setFirstName(client.first_name || "");
        setLastName(client.last_name || "");
        setCompanyName(client.company_name || "");
        setEmail(client.email || "");
        setPhone(client.phone || "");
        setStreet1(client.street_1 || "");
        setStreet2(client.street_2 || "");
        setPostalCode(client.postal_code || "");
        setCity(client.city || "");
        setState(client.state || "");
        setCountry(client.country || "USA");
        // Note: Password is not loaded for security reasons
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load client data");
      navigate("/profile/clients");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!firstName || !lastName || !email || !phone) {
      toast.error("Please fill all required fields");
      return;
    }

    const cleanPhone = phone.replace(/[^+\d]/g, "");

    const payload = {
      password: password || "",
      title: title === "No title" ? "" : title.replace(".", ""),
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      company_name: companyName.trim() || "",
      email: email.trim(),
      phone: cleanPhone,
      street_1: street1.trim() || "",
      street_2: street2.trim() || "",
      city: city.trim() || "",
      state: state.trim() || "",
      postal_code: postalCode.trim() || "",
      country: country.trim() || "USA",
    };

    if (isEditMode) {
      setLoading(true);
      try {
        const response = await clientService.updateClient(id, payload);
        if (response.data.success) {
          toast.success("Client updated successfully");
          navigate("/profile/clients");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to update client");
      } finally {
        setLoading(false);
      }
    } else {
      createClient(payload, {
        onSuccess: () => {
          navigate("/profile/clients");
        },
      });
    }
  };

  return (
    <section className="min-h-screen text-sm">
      <div className="px-4 py-6 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Link
            to="/profile/clients"
            className="p-2 transition rounded-lg hover:bg-gray-100"
          >
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {isEditMode ? "Edit Client" : "New Client"}
            </h1>
            <p className="text-xs text-gray-600">
              {isEditMode ? "Update client information" : "Add a new client to your records"}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="py-6">
        <div className="">
          <div className="overflow-hidden bg-white border border-gray-200 shadow-md rounded-2xl">
            <div className="p-6 space-y-8">
              <div>
                <h2 className="mb-1 text-lg font-bold text-gray-900">
                  Primary contact
                </h2>
                <p className="mb-5 text-xs text-gray-600">
                  Main point of contact for communication
                </p>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Title
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowTitleDropdown(!showTitleDropdown)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-xs font-medium flex items-center justify-between bg-white hover:bg-gray-50"
                      >
                        {title}
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      </button>
                      {showTitleDropdown && (
                        <div className="absolute left-0 z-20 w-full mt-1 text-xs bg-white border rounded-lg shadow-lg top-full">
                          {titles.map((t) => (
                            <button
                              key={t}
                              type="button"
                              onClick={() => {
                                setTitle(t);
                                setShowTitleDropdown(false);
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-gray-50"
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      First name *
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 text-xs"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Last name *
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 text-xs"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Company (optional)
                    </label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 text-xs"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5" /> Phone *
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 text-xs"
                    />
                  </div>
                  {!isEditMode && (
                    <div className="col-span-1 md:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Password
                      </label>
                      <input
                        type="text"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 text-xs"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Client will use this to login
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5" /> Email *
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 text-xs"
                    />
                  </div>
                </div>
              </div>

              <hr className="border-gray-200" />

              <div>
                <h2 className="mb-5 text-lg font-bold text-gray-900">
                  Property address
                </h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <ZipAutoFillInput
                    zip={postalCode}
                    onZipChange={setPostalCode}
                    onAddressResolved={({ city: c, state: s, country: co }) => {
                      if (c) setCity(c);
                      if (s) setState(s);
                      if (co) setCountry(co);
                    }}
                    placeholder="Zip code"
                  />
                  <input
                    type="text"
                    placeholder="Street 1"
                    value={street1}
                    onChange={(e) => setStreet1(e.target.value)}
                    className="px-3 py-2.5 border border-gray-300 rounded-lg text-xs"
                  />
                  <input
                    type="text"
                    placeholder="Street 2 (optional)"
                    value={street2}
                    onChange={(e) => setStreet2(e.target.value)}
                    className="px-3 py-2.5 border border-gray-300 rounded-lg text-xs"
                  />
                  <input
                    type="text"
                    placeholder="State"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="px-3 py-2.5 border border-gray-300 rounded-lg text-xs"
                  />
                  <input
                    type="text"
                    placeholder="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="px-3 py-2.5 border border-gray-300 rounded-lg text-xs"
                  />


                  <input
                    type="text"
                    placeholder="Country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="px-3 py-2.5 border border-gray-300 rounded-lg text-xs"
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <Link
                  to="/profile/clients"
                  className="px-5 py-2.5 border border-gray-300 rounded-lg text-xs font-medium hover:bg-gray-100"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={isCreating || loading}
                  className="px-8 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-xs font-bold shadow-md hover:shadow-lg disabled:opacity-70 flex items-center gap-2"
                >
                  {(isCreating || loading) ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {isEditMode ? "Updating..." : "Creating..."}
                    </>
                  ) : (
                    isEditMode ? "Update client" : "Save client"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </section>
  );
}
